/**
 * Cart offer ("Kassetilbud"): one offer product at a code price, in the mini cart and on the cart page.
 *
 * snippets/cart-offer.liquid renders the offer products hidden. This file shows the visit's offer, adds and
 * removes it (the item and its discount code) and drops the code when the offer line leaves the cart.
 * An add whose code does not apply is rolled back and the offer stays hidden for the rest of the visit.
 * It also keeps the desktop mini cart inside the window (the cart lines give way, then the drawer scrolls).
 * If this file fails the markup just stays hidden and the cart works as normal.
 * Design: robot/docs/cart-offer-design.md.
 */
(function () {
  const OFFER_CODE_PREFIX = 'KASSETILBUD-';
  const MAX_CODES = 5;
  const PRODUCT_KEY = 'cartOffer.productId'; // sessionStorage: the visit's offer
  const HIDDEN_KEY = 'cartOffer.hidden'; // sessionStorage: '1' once an offer code did not apply, no offer for the rest of the visit
  const CODE_KEY = 'cartOffer.codeAdded'; // localStorage: '1' while the theme has put an offer code in the cart
  const BUSY_RELEASE_MS = 10000;
  const PHONE_QUERY = '(max-width: 640px)'; // the theme's phone breakpoint (the drawer is full width below it)
  const MIN_LIST_HEIGHT = 120; // the least height the cart lines in the desktop drawer are squeezed to

  // Used when storage is blocked, so the offer still stays put during this page view.
  const memory = {};

  // The cleanup in flight. An add or remove waits for it, so the cleanup's older code list cannot overwrite a newer one.
  let pendingCleanUp = Promise.resolve();

  function readStorage(area, key) {
    try {
      return window[area].getItem(key);
    } catch (error) {
      return key in memory ? memory[key] : null;
    }
  }

  // A null value removes the key.
  function writeStorage(area, key, value) {
    try {
      if (value === null) {
        window[area].removeItem(key);
      } else {
        window[area].setItem(key, value);
      }
    } catch (error) {
      if (value === null) {
        delete memory[key];
      } else {
        memory[key] = value;
      }
    }
  }

  function isOfferCode(code) {
    return code.toUpperCase().startsWith(OFFER_CODE_PREFIX);
  }

  // The customer's own codes (every offer code dropped), as the comma list /cart/update.js wants, plus the offer code.
  function withoutOfferCodes(codes) {
    return codes.filter((code) => !isOfferCode(code)).join(',');
  }

  function withOfferCode(codes, offerCode) {
    return [withoutOfferCodes(codes), offerCode].filter(Boolean).join(',');
  }

  function codesOf(cart) {
    return cart.discount_codes.map((entry) => entry.code);
  }

  function isOfferLine(item) {
    return Boolean(item.properties && item.properties._cart_offer);
  }

  async function fetchCart() {
    const response = await fetch(`${window.routes.cartUrl}.js`, { cache: 'no-store', credentials: 'same-origin' });

    if (!response.ok) {
      throw new Error(`Cart read failed (${response.status})`);
    }

    return response.json();
  }

  async function postCart(action, body) {
    const response = await fetch(`${window.routes.cartUrl}/${action}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Cart ${action} failed (${response.status})`);
    }

    return response.json();
  }

  // Makes the theme re-render the mini cart (or the cart page) from the server.
  function refreshCart() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } }));
  }

  // Shows one offer in the root: the one already added (layout B), else the visit's stored offer, else a random one.
  // Returns true when the root was revealed. After an offer code failed to apply (HIDDEN_KEY) nothing is offered for
  // the rest of the visit, unless an offer is already in the cart (layout B: it has to stay removable).
  function pickItem(root) {
    const items = [...root.querySelectorAll('[data-cart-offer-item]')];
    const added = items.find((candidate) => candidate.hasAttribute('data-cart-offer-added'));

    if (!items.length || (!added && readStorage('sessionStorage', HIDDEN_KEY) === '1')) {
      return false;
    }

    const storedId = readStorage('sessionStorage', PRODUCT_KEY);
    const item = added || items.find((candidate) => candidate.dataset.productId === storedId) || items[Math.floor(Math.random() * items.length)];

    writeStorage('sessionStorage', PRODUCT_KEY, item.dataset.productId);
    item.hidden = false;
    root.hidden = false;

    return true;
  }

  // The toggle starts unchecked until nomilk.js syncs it from the cookie on window load, so before that the cookie rules.
  function isWithTax() {
    const toggle = document.querySelector('input.tax-setting');

    if (toggle && document.readyState === 'complete') {
      return toggle.checked;
    }

    return !/(?:^|;\s*)check_tax=without_tax(?:;|$)/.test(document.cookie);
  }

  // Every amount is rendered twice; the CSS shows the one that matches data-tax.
  function applyTaxMode(withTax = isWithTax()) {
    document.querySelectorAll('[data-cart-offer]').forEach((root) => {
      root.dataset.tax = withTax ? 'with' : 'without';
    });
  }

  async function addOffer(item, layout) {
    await pendingCleanUp;

    const cart = await fetchCart();
    const codes = codesOf(cart);

    // An offer line is already there (slow re-render, a second tab): never add a second unit, just refresh.
    if (cart.items.some(isOfferLine)) {
      return;
    }

    // Shopify takes 5 codes at most. The block would hide itself on the next render.
    if (codes.filter((code) => !isOfferCode(code)).length >= MAX_CODES) {
      item.closest('[data-cart-offer]').hidden = true;
      throw new Error('The cart already has 5 discount codes');
    }

    await postCart('add', { items: [{ id: Number(item.dataset.variantId), quantity: 1, properties: { _cart_offer: layout } }] });

    try {
      const updated = await postCart('update', { discount: withOfferCode(codes, item.dataset.code) });
      const applied = updated.discount_codes.find((entry) => entry.code.toUpperCase() === item.dataset.code.toUpperCase());

      // The code must be in the cart and applicable (it may not exist, or its conditions may not match this cart).
      if (!applied || applied.applicable === false) {
        writeStorage('sessionStorage', HIDDEN_KEY, '1');
        throw new Error(`Offer code ${item.dataset.code} did not apply, offer removed and hidden for this visit`);
      }
    } catch (error) {
      // Do not leave the offer line in the cart at the normal price under an "offer added" message.
      await removeOffer().catch((rollbackError) => console.warn('Cart offer: rollback failed', rollbackError));
      throw error;
    }

    writeStorage('localStorage', CODE_KEY, '1');
  }

  async function removeOffer() {
    await pendingCleanUp;

    const cart = await fetchCart();
    const line = cart.items.find(isOfferLine);

    if (line) {
      await postCart('change', { id: line.key, quantity: 0 });
    }

    await postCart('update', { discount: withoutOfferCodes(codesOf(cart)) });
    writeStorage('localStorage', CODE_KEY, null);
  }

  // The theme's "Fjern" link removes the offer line without us: drop the offer code once the line is gone.
  async function cleanUpCode() {
    if (readStorage('localStorage', CODE_KEY) !== '1') {
      return;
    }

    try {
      const cart = await fetchCart();

      if (!cart.items.some(isOfferLine)) {
        await postCart('update', { discount: withoutOfferCodes(codesOf(cart)) });
        writeStorage('localStorage', CODE_KEY, null);
      }
    } catch (error) {
      // The flag stays set, so the next re-render tries again.
    }
  }

  // One add / remove per root at a time. The control stays disabled until the cart re-renders (which replaces the
  // root), or until BUSY_RELEASE_MS if no re-render comes. On any error the refresh shows the cart as it really is.
  async function runOnce(root, control, work) {
    if (root.hasAttribute('data-cart-offer-busy')) {
      return;
    }

    root.setAttribute('data-cart-offer-busy', '');
    control.disabled = true;

    try {
      await work();
    } catch (error) {
      console.warn('Cart offer: failed', error);

      if (control.type === 'checkbox') {
        control.checked = !control.checked;
      }
    }

    refreshCart();
    setTimeout(() => {
      root.removeAttribute('data-cart-offer-busy');
      control.disabled = false;
    }, BUSY_RELEASE_MS);
  }

  // Where the drawer's top and bottom edges are at full size. The drawer is scaled to 0.9 while it is closed and grows to 1
  // while it opens; scaling keeps the centre, so the edges are the centre plus or minus half the layout height.
  function drawerEdges(drawer) {
    const rect = drawer.getBoundingClientRect();
    const centre = (rect.top + rect.bottom) / 2;

    return { top: centre - drawer.offsetHeight / 2, bottom: centre + drawer.offsetHeight / 2 };
  }

  // The mini cart sits in the sticky header, so on desktop it has to fit the window by itself. Only while it is open,
  // never on a phone (the theme sizes it there), never on the cart page. First the cart lines give way when the offer
  // block makes the drawer taller than the window (never above the theme's own cap, never below MIN_LIST_HEIGHT).
  // If it is still taller (a one-line cart in a short window) its content scrolls. That second step runs without a
  // visible block too, because the theme's own drawer overflows a short window as well (3 lines at 1366x657).
  // .mini-cart__inner scrolls, not the drawer: overflow on the drawer would clip its nav triangle.
  // Our inline styles are cleared first (closed, phone, fits), so every measurement sees the theme alone.
  function fitDrawer() {
    const drawer = document.querySelector('.mini-cart');
    const inner = drawer && drawer.querySelector('.mini-cart__inner');
    const list = drawer && drawer.querySelector('.mini-cart__line-item-list');

    if (!inner || !list) {
      return;
    }

    list.style.maxHeight = '';
    inner.style.maxHeight = '';
    inner.style.overflowY = '';
    inner.style.overscrollBehavior = '';

    if (window.matchMedia(PHONE_QUERY).matches || drawer.getAttribute('aria-hidden') !== 'false') {
      return;
    }

    if (drawer.querySelector('[data-cart-offer]:not([hidden]), .cart-offer-done')) {
      const themeCap = parseFloat(getComputedStyle(list).maxHeight);
      const overflow = drawerEdges(drawer).bottom - window.innerHeight;

      if (overflow > 0 && themeCap) {
        list.style.maxHeight = `${Math.max(MIN_LIST_HEIGHT, Math.min(themeCap, list.clientHeight - overflow))}px`;
      }
    }

    const { top, bottom } = drawerEdges(drawer);

    if (bottom > window.innerHeight) {
      inner.style.maxHeight = `${Math.max(MIN_LIST_HEIGHT, window.innerHeight - top)}px`;
      inner.style.overflowY = 'auto';
      inner.style.overscrollBehavior = 'contain';
    }
  }

  // Every root on the page (mini cart and cart page both render one); the re-render swaps in fresh roots.
  function init() {
    let revealedInDrawer = false;

    document.querySelectorAll('[data-cart-offer]:not([data-cart-offer-ready])').forEach((root) => {
      if (pickItem(root) && root.classList.contains('cart-offer--drawer')) {
        revealedInDrawer = true;
      }

      root.setAttribute('data-cart-offer-ready', '');
    });

    applyTaxMode();

    // On a phone the theme sized the drawer while the block was still hidden: make it measure again.
    if (revealedInDrawer && window.matchMedia(PHONE_QUERY).matches) {
      window.dispatchEvent(new Event('resize'));
    }

    fitDrawer();
    pendingCleanUp = pendingCleanUp.then(cleanUpCode);
  }

  // The roots are replaced on every re-render, so the listeners sit on the document.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-offer-add]');

    if (button) {
      const item = button.closest('[data-cart-offer-item]');
      const root = item.closest('[data-cart-offer]');

      runOnce(root, button, () => addOffer(item, root.dataset.layout));
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('input.tax-setting')) {
      applyTaxMode(event.target.checked);
    } else if (event.target.matches('[data-cart-offer-tick]')) {
      const tick = event.target;
      const item = tick.closest('[data-cart-offer-item]');
      const root = item.closest('[data-cart-offer]');

      runOnce(root, tick, () => (tick.checked ? addOffer(item, root.dataset.layout) : removeOffer()));
    }
  });

  // The mini cart dispatches cart:rerendered on the header element without bubbling, so listen in the capture phase.
  document.addEventListener('cart:rerendered', init, true);
  window.addEventListener('load', () => applyTaxMode());
  window.addEventListener('resize', fitDrawer);

  // Measure again whenever the drawer opens or closes (the header can have grown since load, the Trustpilot widget).
  const miniCart = document.querySelector('.mini-cart');

  if (miniCart) {
    new MutationObserver(fitDrawer).observe(miniCart, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
